import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type SliderSegment = {
  id: string;
  period: number;
  label: string;
  startSec: number;
  endSec: number;
  available: boolean;
  dividerBefore?: boolean;
};

type Props = {
  segments: SliderSegment[];
  value: number; // current seconds
  max: number; // total seconds
  onChange: (seconds: number) => void;
  onTimeLabel: (seconds: number) => string;
  popperRef: React.MutableRefObject<any>;
  positionRef: React.MutableRefObject<{ x: number; y: number }>;
  setTooltipTime: (time: string) => void;
};

const SEGMENT_GAP = 2;
const TRACK_HEIGHT = 4;
const TRACK_HEIGHT_ACTIVE = 7;
const TRACK_HEIGHT_HOVER = 8;
const THUMB_SIZE = 0;
const THUMB_SIZE_HOVER = 14;
const THUMB_SIZE_ACTIVE = 16;
const BORDER_RADIUS = '0px';

const SegmentedSlider: React.FC<Props> = ({
  segments,
  value,
  max,
  onChange,
  onTimeLabel,
  popperRef,
  positionRef,
  setTooltipTime,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredSegmentIdx, setHoveredSegmentIdx] = useState<number | null>(null);

  const totalDuration = max;

  const gapAfter = useCallback(
    (index: number) => (segments[index + 1]?.dividerBefore ? SEGMENT_GAP : 0),
    [segments],
  );

  const totalGapWidth = useMemo(
    () => segments.reduce((total, segment) => total + (segment.dividerBefore ? SEGMENT_GAP : 0), 0),
    [segments],
  );

  // Find which segment the current value falls into
  const activeSegmentIdx = segments.findIndex(
    (s) => s.available && value >= s.startSec && value <= s.endSec,
  );

  // Compute the fill percentage for a given segment
  const getFillPercent = (seg: SliderSegment, idx: number): number => {
    // Value is outside every segment's [startSec, endSec] range
    // (e.g. live edge ahead of last mapping, or seek into an intermission gap).
    // Decide per-segment by comparing value to that segment's bounds.
    if (activeSegmentIdx === -1) {
      return value > seg.endSec ? 100 : 0;
    }
    if (idx < activeSegmentIdx) return 100;
    if (idx > activeSegmentIdx) return 0;
    // Current segment
    const duration = seg.endSec - seg.startSec;
    if (duration <= 0) return 0;
    return Math.min(100, Math.max(0, ((value - seg.startSec) / duration) * 100));
  };

  // Convert a clientX position to seconds within the segmented timeline
  const clientXToSeconds = useCallback(
    (clientX: number): number => {
      const container = containerRef.current;
      if (!container || segments.length === 0) return 0;

      const rect = container.getBoundingClientRect();
      const trackWidth = rect.width - totalGapWidth;

      let x = clientX - rect.left;
      x = Math.max(0, Math.min(x, rect.width));

      // Walk segments left-to-right to find which one was clicked
      let accumulatedX = 0;
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const segDuration = seg.endSec - seg.startSec;
        const segWidth = totalDuration > 0 ? (segDuration / totalDuration) * trackWidth : 0;
        const gap = gapAfter(i);

        if (x <= accumulatedX + segWidth) {
          // Click is within this segment
          const localX = x - accumulatedX;
          const ratio = segWidth > 0 ? localX / segWidth : 0;
          return seg.startSec + ratio * segDuration;
        }

        accumulatedX += segWidth + gap;
      }

      // Past the end — return max
      return segments[segments.length - 1].endSec;
    },
    [gapAfter, segments, totalDuration, totalGapWidth],
  );

  // Find the hovered segment index for a given clientX
  const clientXToSegmentIdx = useCallback(
    (clientX: number): number | null => {
      const container = containerRef.current;
      if (!container || segments.length === 0) return null;

      const rect = container.getBoundingClientRect();
      const trackWidth = rect.width - totalGapWidth;

      let x = clientX - rect.left;
      x = Math.max(0, Math.min(x, rect.width));

      let accumulatedX = 0;
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const segDuration = seg.endSec - seg.startSec;
        const segWidth = totalDuration > 0 ? (segDuration / totalDuration) * trackWidth : 0;
        const gap = gapAfter(i);

        if (x <= accumulatedX + segWidth) return i;
        accumulatedX += segWidth + gap;
      }
      return segments.length - 1;
    },
    [gapAfter, segments, totalDuration, totalGapWidth],
  );

  // Compute the thumb's pixel offset from the left of the container
  const getThumbLeft = useCallback((): number => {
    const container = containerRef.current;
    if (!container || segments.length === 0) return 0;

    const rect = container.getBoundingClientRect();
    const trackWidth = rect.width - totalGapWidth;

    let accumulatedX = 0;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const segDuration = seg.endSec - seg.startSec;
      const segWidth = totalDuration > 0 ? (segDuration / totalDuration) * trackWidth : 0;
      const gap = gapAfter(i);

      if (value >= seg.startSec && value <= seg.endSec) {
        const ratio = segDuration > 0 ? (value - seg.startSec) / segDuration : 0;
        return accumulatedX + ratio * segWidth;
      }

      accumulatedX += segWidth + gap;
    }

    return accumulatedX;
  }, [gapAfter, segments, totalDuration, totalGapWidth, value]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      const seconds = clientXToSeconds(e.clientX);
      onChange(seconds);
    },
    [clientXToSeconds, onChange],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const seconds = clientXToSeconds(e.clientX);
      const displayTime = onTimeLabel(seconds);
      setTooltipTime(displayTime);
      setHoveredSegmentIdx(clientXToSegmentIdx(e.clientX));

      const container = containerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        positionRef.current = { x: e.clientX, y: rect.y };
        if (popperRef.current != null) {
          popperRef.current.update();
        }
      }
    },
    [clientXToSeconds, clientXToSegmentIdx, onTimeLabel, setTooltipTime, positionRef, popperRef],
  );

  // Global mouse events for dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMove = (e: MouseEvent) => {
      const seconds = clientXToSeconds(e.clientX);
      onChange(seconds);

      const displayTime = onTimeLabel(seconds);
      setTooltipTime(displayTime);
      setHoveredSegmentIdx(clientXToSegmentIdx(e.clientX));

      const container = containerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        positionRef.current = { x: e.clientX, y: rect.y };
        if (popperRef.current != null) {
          popperRef.current.update();
        }
      }
    };

    const handleGlobalUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleGlobalMove);
    window.addEventListener('mouseup', handleGlobalUp);
    return () => {
      window.removeEventListener('mousemove', handleGlobalMove);
      window.removeEventListener('mouseup', handleGlobalUp);
    };
  }, [
    isDragging,
    clientXToSeconds,
    clientXToSegmentIdx,
    onChange,
    onTimeLabel,
    setTooltipTime,
    positionRef,
    popperRef,
  ]);

  // Re-render on resize so the thumb position stays correct
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const onResize = () => forceUpdate((n) => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const thumbLeft = getThumbLeft();
  const thumbSize = isDragging ? THUMB_SIZE_ACTIVE : isHovered ? THUMB_SIZE_HOVER : THUMB_SIZE;

  const railColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';

  return (
    <Box
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setHoveredSegmentIdx(null);
      }}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: 20,
        cursor: hoveredSegmentIdx !== null && !segments[hoveredSegmentIdx]?.available ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        gap: 0,
      }}
    >
      {segments.map((seg, idx) => {
        const segDuration = seg.endSec - seg.startSec;
        const flexGrow = totalDuration > 0 ? segDuration / totalDuration : 1;
        const fillPercent = getFillPercent(seg, idx);
        const isActive = idx === activeSegmentIdx;
        const isHoveredSeg = idx === hoveredSegmentIdx;

        let height = TRACK_HEIGHT;
        if (isActive) height = TRACK_HEIGHT_ACTIVE;
        if (isHoveredSeg || isDragging) height = TRACK_HEIGHT_HOVER;

        return (
          <Box
            key={seg.id}
            sx={{
              position: 'relative',
              flexGrow,
              flexShrink: 1,
              flexBasis: 0,
              marginLeft: seg.dividerBefore ? `${SEGMENT_GAP}px` : 0,
              height: `${height}px`,
              borderRadius: BORDER_RADIUS,
              background: seg.available
                ? railColor
                : 'repeating-linear-gradient(135deg, rgba(255,255,255,0.12) 0 4px, rgba(0,0,0,0.28) 4px 8px)',
              overflow: 'hidden',
              transition: 'height 0.15s ease',
            }}
          >
            {seg.available && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: `${fillPercent}%`,
                  backgroundColor: '#ff0000',
                  borderRadius: BORDER_RADIUS,
                  transition: isDragging ? 'none' : 'width 0.1s linear',
                }}
              />
            )}
          </Box>
        );
      })}
      {/* Thumb */}
      <Box
        sx={{
          position: 'absolute',
          left: `${thumbLeft}px`,
          top: '50%',
          width: `${thumbSize}px`,
          height: `${thumbSize}px`,
          borderRadius: '50%',
          backgroundColor: '#ff0000',
          transform: 'translate(-50%, -50%)',
          transition: isDragging ? 'none' : 'width 0.15s ease, height 0.15s ease, left 0.1s linear',
          boxShadow: thumbSize > 0 ? '0 2px 12px 0 rgba(0,0,0,0.4)' : 'none',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default SegmentedSlider;
